import {
  durationHours,
  durationDays,
  requestPriceOnRequest,
} from '@bells/i18n';
import { type Locale } from '@bells/i18n/runtime';
import {
  AttendantSearchSingleResponse,
  PriceRequest,
} from '@bells/schema/models/attendant';
import { last } from 'lodash';

export const calculatePrice = (
  duration: number,
  prices: PriceRequest | null
) => {
  if (!prices || !prices.prices) {
    return undefined;
  }
  const defaultPrice = prices.prices.find((df) => df.hours == duration);

  if (defaultPrice) {
    return defaultPrice.price ?? undefined;
  }

  if (duration >= 48 && prices.perExtraDay) {
    const lastPrice = last(
      prices.prices.filter((df) => df.hours >= 24 && df.price)
    );
    if (lastPrice && lastPrice.price) {
      const diff = (duration - lastPrice.hours) / 24;
      return lastPrice.price + prices.perExtraDay * diff;
    }
  }

  return undefined;
};

export const moneyFormatter = Intl.NumberFormat('de-DE', {
  currency: 'EUR',
  currencyDisplay: 'symbol',
  currencySign: 'standard',
  style: 'currency',
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
  minimumIntegerDigits: 1,
});

export const formatDurationPlane = (val: number, locale: Locale) => {
  let formattedHours = '';
  if (val < 24) {
    formattedHours = durationHours({ count: val });
  } else {
    formattedHours = durationDays({ count: val / 24 }, { locale });
  }

  return formattedHours;
};

export const formatDuration = (
  val: number,
  locale: Locale,
  data: AttendantSearchSingleResponse
) => {
  const price = calculatePrice(val, {
    prices: data.prices ?? null,
    perExtraDay: data.perExtraDay,
    ffmPrice: data.ffmPrice,
    mmfPrice: data.mmfPrice,
    swingerPrice: data.swingerPrice,
    gangbangPrice: data.gangbangPrice,
  });
  let formattedHours = formatDurationPlane(val, locale);

  return price
    ? `${formattedHours} (${moneyFormatter.format(price)})`
    : `${formattedHours} (${requestPriceOnRequest()})`;
};

export function formatDateIntl(date: Date): string {
  return new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function formatDateOnlyIntl(date: Date): string {
  return new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'medium',
    timeStyle: undefined,
  }).format(date);
}

export function formatDateIntlLong(date: Date | string): string {
  return new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(typeof date === 'string' ? new Date(date) : date);
}

export function formatTimeOnly(date: Date): string {
  return new Intl.DateTimeFormat(navigator.language, {
    timeStyle: 'short',
  }).format(date);
}

export function formatDateOnly(date: Date): string {
  return new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'medium',
  }).format(date);
}
